import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Copy, Dices, Gem, Sparkles } from 'lucide-react'
import toast from 'react-hot-toast'
import apiService from '../../../services/api'
import { hapticNotify } from '../../../utils/haptic'

// Порядок символов math_ver 1 (slot:config.symbols: 0–9 = L1,L2,L3,L4,H1,H2,H3,H4,W,S).
// Названия для карточки берём из переводов slot.sym_*, W не переводится (гайд пакета).
// Цвета младших символов и WILD — как в пакете (kit/slot.css: .slt-s0…s3, .slt-wild)
const SYMBOLS = {
  0: { key: 'L1', glyph: '♠︎', className: 'text-[#7fb2ff]' },
  1: { key: 'L2', glyph: '♣︎', className: 'text-[#63e6a1]' },
  2: { key: 'L3', glyph: '♦︎', className: 'text-[#ffb84d]' },
  3: { key: 'L4', glyph: '♥︎', className: 'text-[#ff6f8e]' },
  4: { key: 'H1', glyph: '🎯' },
  5: { key: 'H2', glyph: '💣' },
  6: { key: 'H3', glyph: '🗡️' },
  7: { key: 'H4', glyph: '👑' },
  8: { key: 'W', glyph: 'WILD', className: 'text-[11px] font-black tracking-wide text-[#ffd24a] sm:text-sm' },
  9: { key: 'S', glyph: '💎' }
}

// Цвет линии — та же формула, что в пакете (lineColor в kit/slot-game.js), чтобы совпадать с игрой
const lineColor = (line) => `hsl(${(line * 47) % 360} 95% 62%)`
// Небольшой вертикальный сдвиг, чтобы линии по одному ряду не сливались (как превью линий в пакете)
const lineShift = (line) => ((line % 5) - 2) * 7
const SYMBOL_NAME_KEYS = {
  L1: 'slot.sym_L1', L2: 'slot.sym_L2', L3: 'slot.sym_L3', L4: 'slot.sym_L4',
  H1: 'slot.sym_H1', H2: 'slot.sym_H2', H3: 'slot.sym_H3', H4: 'slot.sym_H4'
}

// ID спина: «код сессии»-«номер», код — 7–8 символов [0-9a-z]
const SPIN_ID_PATTERN = /^[0-9a-z]{7,8}-\d+$/

/**
 * История спинов слота (гайд, «История спинов (REST)»):
 * список последних 100 спинов — GET /api/slot/history/,
 * карточка одного спина — GET /api/slot/spin/{id}/.
 */
export default function SlotHistory() {
  const { id } = useParams()
  const { t, i18n } = useTranslation()
  const [spins, setSpins] = useState([])
  const [spin, setSpin] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const money = (amount) => Number(amount || 0).toLocaleString(i18n.language)

  useEffect(() => {
    let active = true
    setLoading(true)
    setError(false)
    const endpoint = id && SPIN_ID_PATTERN.test(id) ? `/api/slot/spin/${id}/` : id ? null : '/api/slot/history/'
    if (!endpoint) { setLoading(false); setError(true); return undefined }
    apiService.makeRequest(endpoint, { method: 'GET', skipBalanceSync: true }).then((data) => {
      if (active) { setSpins(data.spins || []); setSpin(data.spin || null) }
    }).catch(() => { if (active) setError(true) }).finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [id])

  const handleCopyId = async () => {
    if (!spin) return
    try {
      await navigator.clipboard.writeText(spin.id || id)
      hapticNotify('success')
      toast.success(t('slot.history.copied'))
    } catch {
      hapticNotify('error')
      toast.error(t('slot.history.copyFailed'))
    }
  }

  const symbolName = (symbolId) => {
    const symbol = SYMBOLS[symbolId]
    const key = symbol && SYMBOL_NAME_KEYS[symbol.key]
    return key ? t(key) : String(symbolId)
  }

  return (
    <div className="mx-auto max-w-xl space-y-4 px-4 py-6 text-white">
      <div className="flex items-center gap-3">
        <Link to={id ? '/slot/history' : '/slot'} aria-label={t('common.back')} className="rounded-xl border border-white/10 bg-surface p-2"><ArrowLeft size={20} className="rtl:rotate-180" /></Link>
        <h1 className="text-2xl font-black text-accent-primary">{t('slot.history.title')}</h1>
        {id && spin && (
          <button type="button" onClick={handleCopyId} aria-label={t('slot.history.copyId')} title={t('slot.history.copyId')} className="ms-auto rounded-xl border border-white/10 bg-surface p-2 text-white transition-colors hover:border-accent-primary/40 hover:text-accent-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-primary"><Copy size={20} aria-hidden="true" /></button>
        )}
      </div>

      {loading
        ? <p className="text-text-secondary">{t('slot.loading')}</p>
        : error
          ? <p role="alert" className="text-red-400">{t('slot.history.error')}</p>
          : id
            ? (spin
                ? <SpinCard spin={spin} money={money} t={t} symbolName={symbolName} />
                : <p>{t('slot.history.error')}</p>)
            : (spins.length
                ? spins.map((entry) => <SpinEntry key={entry.id} entry={entry} money={money} t={t} />)
                : <p className="text-text-secondary">{t('slot.history.empty')}</p>)}
    </div>
  )
}

function SpinEntry({ entry, money, t }) {
  const won = Number(entry.total_win) > 0
  const ago = Math.max(1, Math.floor((Date.now() / 1000 - Number(entry.created_at || 0)) / 60))
  return (
    <Link to={`/slot/history/${entry.id}`} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-surface p-3 transition-colors hover:border-accent-primary/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-primary">
      <Dices size={36} className="shrink-0 text-text-secondary" aria-hidden="true" />
      <div className="flex min-w-0 flex-1 flex-col justify-center">
        <span className="flex flex-wrap items-center gap-2 text-xs text-text-secondary">
          <bdi dir="ltr" className="font-mono">{entry.id}</bdi>
          {entry.bonus && <span className="inline-flex items-center gap-1 rounded-full bg-purple-500/20 px-2 py-0.5 text-[11px] font-semibold text-purple-300"><Sparkles size={12} aria-hidden="true" />{t('slot.history.bonus')}</span>}
        </span>
        <span className="mt-2 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-text-secondary">{t('slot.total_bet')}: <bdi dir="ltr" className="tabular-nums">{money(entry.bet)}</bdi></span>
          <span className={won ? 'inline-flex items-center gap-1 font-bold text-accent-primary' : 'text-text-secondary'}>
            {won && <Gem size={16} className="shrink-0" aria-hidden="true" />}
            <bdi dir="ltr" className="tabular-nums">{won ? '+' : ''}{money(entry.total_win)}</bdi>
            <bdi dir="ltr"> · ×{entry.x}</bdi>
          </span>
        </span>
        <span className="mt-2 text-[11px] text-text-secondary">{t('slot.history.ago', { minutes: ago })}</span>
      </div>
    </Link>
  )
}

function SpinCard({ spin, money, t, symbolName }) {
  const winningCells = new Set()
  for (const win of spin.wins || []) for (const [reel, row] of win.cells || []) winningCells.add(`${reel}:${row}`)
  const scatterCells = new Set((spin.scatter?.cells || []).map(([reel, row]) => `${reel}:${row}`))
  const bet = Number(spin.bet || 0)
  const totalWin = Number(spin.total_win || 0)
  const x = bet > 0 ? (totalWin / bet).toFixed(2) : '0'
  const bonusSpins = spin.bonus?.spins?.length || 0

  return (
    <div className="space-y-4">
      {/* Поле 5×3: grid[барабан][ряд], поле всегда слева направо (линии считаются от левого барабана) */}
      <div dir="ltr" className="relative rounded-2xl border border-white/10 bg-dark-primary p-2">
        <div className="grid grid-cols-5 gap-1.5">
          {Array.from({ length: 3 }, (_, row) => Array.from({ length: 5 }, (_, reel) => {
            const symbol = SYMBOLS[spin.grid?.[reel]?.[row]]
            const isWin = winningCells.has(`${reel}:${row}`)
            const isScatter = scatterCells.has(`${reel}:${row}`)
            return (
              <span
                key={`${reel}:${row}`}
                className={`flex aspect-square items-center justify-center rounded-xl border text-2xl sm:text-3xl
                  ${isScatter ? 'border-yellow-400 bg-yellow-400/15 shadow-[0_0_12px_rgba(255,210,74,.35)]'
                    : isWin ? 'border-accent-primary bg-accent-primary/15 shadow-[0_0_12px_rgba(0,255,127,.3)]'
                      : 'border-white/5 bg-white/5 text-white'}`}
              >
                <span className={symbol?.className}>{symbol ? symbol.glyph : '·'}</span>
              </span>
            )
          }))}
        </div>
        {/* Выигрышные линии поверх поля: через сыгравшие клетки win.cells (сетка 500×300, клетка — 100×100) */}
        {(spin.wins || []).length > 0 && (
          <svg viewBox="0 0 500 300" preserveAspectRatio="none" className="pointer-events-none absolute inset-2 h-[calc(100%-1rem)] w-[calc(100%-1rem)] overflow-visible" aria-hidden="true">
            {spin.wins.map((win, index) => (
              <polyline
                key={index}
                points={(win.cells || []).map(([reel, row]) => `${reel * 100 + 50},${row * 100 + 50 + lineShift(win.line)}`).join(' ')}
                fill="none"
                stroke={lineColor(win.line)}
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
                opacity="0.9"
                style={{ filter: `drop-shadow(0 0 4px ${lineColor(win.line)})` }}
              />
            ))}
          </svg>
        )}
      </div>

      {/* Выигрышные линии: суммы приходят готовыми, клиент не пересчитывает */}
      {(spin.wins || []).length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-surface p-3">
          <span className="block text-xs font-semibold uppercase tracking-widest text-text-secondary">{t('slot.history.winsTitle')}</span>
          <ul className="mt-2 space-y-1 text-sm">
            {(spin.wins || []).map((win, index) => (
              <li key={index} className="flex items-center justify-between gap-2">
                <span className="inline-flex min-w-0 items-center gap-2">
                  <bdi dir="ltr" className="rounded-lg border bg-white/5 px-1.5 py-0.5 text-[11px] font-bold tabular-nums" style={{ color: lineColor(win.line), borderColor: lineColor(win.line) }}>#{win.line + 1}</bdi>
                  <span className="truncate">{symbolName(win.symbol)} ×{win.count}</span>
                </span>
                <span className="inline-flex shrink-0 items-center gap-1 font-bold tabular-nums text-accent-primary"><Gem size={13} aria-hidden="true" /><bdi dir="ltr">{money(win.amount)}</bdi></span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {spin.scatter && (
        <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-surface p-3 text-sm">
          <span className="inline-flex items-center gap-2 text-yellow-400"><Sparkles size={16} aria-hidden="true" />{t('slot.history.scatter', { count: spin.scatter.count })}</span>
          <span className="inline-flex items-center gap-1 font-bold tabular-nums text-yellow-400"><Gem size={13} aria-hidden="true" /><bdi dir="ltr">{money(spin.scatter.amount)}</bdi></span>
        </div>
      )}

      {spin.bonus && (
        <div className="rounded-2xl border border-purple-500/30 bg-purple-500/10 p-3 text-sm">
          <span className="block text-xs font-semibold uppercase tracking-widest text-purple-300">{t('slot.history.bonus')}</span>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <span className="text-white">{t('slot.history.freeSpins', { count: bonusSpins })}</span>
            <span className="inline-flex items-center gap-1 font-black tabular-nums text-purple-300"><Gem size={15} aria-hidden="true" /><bdi dir="ltr">{money(spin.bonus.total)}</bdi></span>
          </div>
        </div>
      )}

      {/* Итоги: ставка, выигрыш спина и раунда целиком (с бонуской) */}
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3 border-b border-white/20 px-1 pb-5 pt-2">
        <div className="min-w-0 max-w-full">
          <span className="block text-xs text-text-secondary">{t('slot.total_bet')}</span>
          <span className="mt-1 inline-flex max-w-full items-start gap-1 text-xl font-bold text-white sm:text-2xl"><Gem size={17} className="mt-1 shrink-0" aria-hidden="true" /><bdi dir="ltr" className="min-w-0 break-all tabular-nums">{money(spin.bet)}</bdi></span>
          <span className="mt-1 block text-[11px] text-text-secondary">{t('slot.bet_per_line')} <bdi dir="ltr">{money(spin.bet_per_line)}</bdi> · {t('slot.lines')} <bdi dir="ltr">{spin.lines}</bdi></span>
        </div>
        <div className="min-w-0 max-w-full text-end">
          <span className="block text-xs text-text-secondary">{totalWin > 0 ? t('slot.history.win') : t('slot.history.noWin')} · <bdi dir="ltr">×{x}</bdi></span>
          <span className={`mt-1 inline-flex max-w-full items-start justify-end gap-1 text-xl font-black sm:text-2xl ${totalWin > 0 ? 'text-accent-primary' : 'text-text-secondary'}`}><Gem size={17} className="mt-1 shrink-0" aria-hidden="true" /><bdi dir="ltr" className="min-w-0 break-all tabular-nums">{money(totalWin)}</bdi></span>
        </div>
      </div>
    </div>
  )
}
